import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CartInfoState } from '../CartInfoState';
import { getItemsByDeliveryType } from '../utils';

import { useRootSelector } from 'rootStore';
import { useShippingMethods } from 'common/hooks/useShippingMethods';
import { useDefaultAddress } from 'common/hooks/useDefaultAddress';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { ShippingMethodsResponse, ShippingOption } from 'common/api/e-comm/orders/service';
import { requestCartItems } from 'common/features/store/duck/cart/duck';
import {
    emptyFamilyAddress,
    FamilyAddress,
} from 'common/api/crm/family/address/models/FamilyAddress';
import { AsyncActionState } from 'common/modules/async-actions/core';
import { APIError } from 'common/api/models';
import { useAddresses } from 'common/hooks/useAddresses';
import { ProductType } from 'common/api/e-comm/models/Product';
import { useEffectUntilDone } from 'hooks/useEffectUntilDone';

export const useCartShipping = (cartInfo: CartInfoState): CartInfoShipping => {
    const homeDetails = useHomeDetails();
    const { cart, cartAsyncState } = useRootSelector((s) => s.store.cart);
    const { shipToHome, shippingToAddressId } = cartInfo;
    const dispatch = useDispatch<any>();
    const { addresses, asyncState, refreshAddresses } = useAddresses();

    const { refreshDefaultAddress, defaultAddress, defaultAddressesAsyncState } =
        useDefaultAddress();

    const {
        loadShippingMethods,
        shippingMethodsResponse,
        loading: isLoadingShippingMethods,
    } = useShippingMethods();

    const requestedCartItemsRef = useRef(false);
    const refreshedDefaultAddressRef = useRef(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const lineItems = cart?.lineItems || [];
    const hasShipToHomeEligibleItems = Boolean(
        lineItems.find(
            (item) =>
                item.product.type === ProductType.GiftCard && item.product.isShipToHomeEligible
        )
    );

    const { shipToHomeItems } = useMemo(
        () => getItemsByDeliveryType(lineItems, Boolean(shipToHome)),
        [lineItems, shipToHome]
    );

    useEffect(() => {
        if (!cart && !requestedCartItemsRef.current) {
            requestedCartItemsRef.current = true;
            requestCartItems()(dispatch);
        }
    }, [cart, dispatch]);

    useEffect(() => {
        if (!defaultAddress && !refreshedDefaultAddressRef.current) {
            refreshedDefaultAddressRef.current = true;
            refreshDefaultAddress();
        }
    }, [defaultAddress, refreshedDefaultAddressRef, refreshDefaultAddress]);

    useEffect(() => {
        if (!addresses) {
            refreshAddresses();
        }
    }, [addresses, refreshAddresses]);

    const selectedAddress = useMemo(
        () =>
            (addresses &&
                shippingToAddressId &&
                addresses.find((addr) => addr.id === shippingToAddressId)) ||
            defaultAddress,
        [addresses, shippingToAddressId, defaultAddress]
    );

    const cartId = useMemo(() => cart?.id || '', [cart]);

    const shipToHomeIfPossible = useMemo(
        () => shipToHome && shipToHomeItems.length > 0,
        [shipToHome, shipToHomeItems]
    );

    const refreshShippingMethods = useCallback(
        async (address: FamilyAddress) => {
            if (cartId && homeDetails && address) {
                await loadShippingMethods({
                    cartId,
                    customerFirstName: homeDetails.profile.firstName,
                    customerLastName: homeDetails.profile.lastName,
                    customerEmail: homeDetails.profile.email,
                    shipToHomeLineItemIds: shipToHomeIfPossible
                        ? shipToHomeItems.map((li) => li.id)
                        : undefined,
                    shippingAddress: { email: homeDetails.profile.email, ...address },
                });
            }
        },
        [cartId, homeDetails, loadShippingMethods, shipToHomeItems, shipToHomeIfPossible]
    );

    useEffectUntilDone(
        (markDone: () => void) => {
            if (defaultAddress && cartId && homeDetails) {
                markDone();
                refreshShippingMethods(defaultAddress);
            }
        },
        [defaultAddress, homeDetails, cartId]
    );

    const shippingOptions = useMemo(() => {
        return shippingMethodsResponse && shippingMethodsResponse.shipToHomeConsignment
            ? shippingMethodsResponse.shipToHomeConsignment.shippingOptions
            : [];
    }, [shippingMethodsResponse]);

    const cheapestShipToHome = useMemo(() => {
        if (!shippingOptions || shippingOptions.length <= 0) {
            return;
        }
        return shippingOptions.sort((sm1, sm2) => sm1.cost - sm2.cost)[0];
    }, [shippingOptions]);

    const selectedShipping = useMemo(() => {
        if (!shipToHomeIfPossible) {
            return;
        }

        if (cartInfo.shippingMethodId) {
            return shippingOptions.find((opt) => opt.id === cartInfo.shippingMethodId);
        }
        return cheapestShipToHome;
    }, [shipToHomeIfPossible, shippingOptions, cheapestShipToHome, cartInfo.shippingMethodId]);

    const shippingFee = useMemo(() => {
        return selectedShipping?.cost || 0;
    }, [selectedShipping]);

    return {
        isLoading:
            cartAsyncState.loading ||
            defaultAddressesAsyncState.loading ||
            isLoadingShippingMethods,
        hasShipToHomeEligibleItems,
        shippingFee,
        shippingOptions,
        selectedShipping,
        selectedAddress,
        defaultAddress,
        defaultAddressesAsyncState,
        userAddresses: {
            addresses,
            isLoading: asyncState.loading,
            refreshAddresses,
        },
        shippingMethodsResponse,
        refreshShippingMethods,
    };
};

export interface CartInfoShipping {
    isLoading: boolean;
    hasShipToHomeEligibleItems: boolean;
    shippingFee: number;
    shippingOptions: ShippingOption[];
    selectedShipping: ShippingOption | undefined;
    selectedAddress: FamilyAddress | null;
    defaultAddress: FamilyAddress | null;
    defaultAddressesAsyncState: AsyncActionState<[], APIError<null>> | undefined;
    userAddresses: {
        addresses: FamilyAddress[] | null;
        isLoading: boolean;
        refreshAddresses: () => Promise<boolean>;
    };
    shippingMethodsResponse: ShippingMethodsResponse | null | undefined;
    refreshShippingMethods?: (address: FamilyAddress) => Promise<void>;
}

export const emptyCartInfoShipping: CartInfoShipping = {
    isLoading: false,
    hasShipToHomeEligibleItems: false,
    shippingFee: 0,
    shippingOptions: [],
    selectedShipping: undefined,
    selectedAddress: null,
    defaultAddress: emptyFamilyAddress,
    defaultAddressesAsyncState: undefined,
    userAddresses: {
        addresses: [],
        isLoading: false,
        refreshAddresses: () => Promise.reject(),
    },
    shippingMethodsResponse: undefined,
    refreshShippingMethods: () => Promise.reject(),
};
